import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import {
  managedServicesTickets,
  orderTrackingTable,
  repairServicesTicketTableRoute,
} from 'src/app/core/constants/routes';
import { SharedFilterDataService } from 'src/app/core/services/shared-filter-data.service';
import {
  FilterType,
  GlobalSearchResults,
  ResultsDatum,
  ResultsDatumUI,
} from 'src/app/features/secured-skeleton/models/global-search';
import { SupportSharedDataService } from 'src/app/features/support/services/support-shared-data.service';

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss'],
  standalone: false,
})
export class SearchResultsComponent implements OnInit {
  @Input() searchResult: GlobalSearchResults;
  @Input() renderFilterType: (filterType: FilterType) => string;
  @Output() onResultClickedEmitter = new EventEmitter<ResultsDatum>();
  results: ResultsDatum[] = [];
  resultsUI: ResultsDatumUI[] = [];

  titleIcon: string;
  total: number;
  serviceAddon: string;
  foundFilterTypes: FilterType[];

  titleUI: string;
  browseTitleUI: string;
  numberOfResultsUI: string;
  foundFilterTypesUI: string;

  hasNoModulePermission: boolean = false;

  constructor(
    private router: Router,
    private supportSharedData: SupportSharedDataService,
    private orderTrackingSharedDate: SharedFilterDataService,
  ) {}

  ngOnInit(): void {
    this.results = this.searchResult.results.data;
    this.total = this.searchResult.results.total;
    this.serviceAddon = this.searchResult.serviceAddon;
    this.foundFilterTypes = this.searchResult.results.foundFilterTypes;
    this.titleIcon = this.renderTitleIcon(this.searchResult.serviceAddon);
    this.hasNoModulePermission = this.results.length === 0 && this.total > 0;

    this.titleUI = this.renderTitle(this.searchResult.serviceAddon);
    this.browseTitleUI = this.renderBrowseTitle(this.searchResult.serviceAddon);
    this.numberOfResultsUI = this.renderNumberOfResults(this.total);
    this.resultsUI = this.results.slice(0, 5).map((result) => {
      return {
        ...result,
        resultTextArray: this.formatResultTextIntoArray(result.resultText, result.bestPrecisionKeyword),
      };
    });
    this.foundFilterTypesUI = this.renderFoundFields(this.foundFilterTypes);
  }

  /**
   * The function handles different scenarios based on the serviceAddon value and navigates to
   * different routes accordingly.
   * @param {ResultsDatum} item - The `item` parameter is an object of type `ResultsDatum`. It contains
   * information about the clicked result, such as the result type, result text, and navigation
   * identifier.
   */
  onResultClicked(item: ResultsDatum) {
    this.onResultClickedEmitter.emit();

    //check serviceAddon
    if (this.serviceAddon === 'repair-services') {
      if (item.navigationIdentifier) {
        this.router.navigate([repairServicesTicketTableRoute, item.navigationIdentifier]);
      }

      if (!item.navigationIdentifier) {
        this.supportSharedData.ticketsTableData.ticketStatus = 'both';
        const currentRoute = this.router.url.split('?')[0];
        const isInRepairServiceView = currentRoute.includes(repairServicesTicketTableRoute);

        if (item.resultType === 'customer-ticket-number') {
          this.supportSharedData.ticketsTableData.customerTicketNumber = item.resultText;
          //check if active route is repair-services-ticket-table
          this.router.navigate([repairServicesTicketTableRoute], {
            queryParams: {
              reload: isInRepairServiceView ? item.resultText : undefined,
            },
          });
        }
        if (item.resultType === 'asset-tag') {
          this.supportSharedData.ticketsTableData.assetTag = item.resultText;
          this.router.navigate([repairServicesTicketTableRoute], {
            queryParams: {
              reload: isInRepairServiceView ? item.resultText : undefined,
            },
          });
        }
        if (item.resultType === 'serial-numbers') {
          this.supportSharedData.ticketsTableData.serialNumber = item.resultText;
          this.router.navigate([repairServicesTicketTableRoute], {
            queryParams: {
              reload: isInRepairServiceView ? item.resultText : undefined,
            },
          });
        }
      }
    }

    if (this.serviceAddon === 'order-tracking') {
      if (item.navigationIdentifier) {
        if (item.resultType === 'cpo' || item.resultType === 'tracking-numbers') {
          this.router.navigate([orderTrackingTable, item.navigationIdentifier]);
        }

        if (item.resultType === 'serial-numbers') {
          this.router.navigate([orderTrackingTable, item.navigationIdentifier], {
            queryParams: {
              'open-serial-dialog': item.resultText,
            },
          });
        }
      }

      if (!item.navigationIdentifier) {
        if (item.resultType === 'cpo') {
          this.orderTrackingSharedDate.data.cpoQuery = item.resultText;
          this.router.navigate([orderTrackingTable], {
            queryParams: {
              'cpo-query': item.resultText,
            },
          });
        }

        if (item.resultType === 'tracking-numbers') {
          this.orderTrackingSharedDate.data.trackingNumberQuery = item.resultText;
          this.router.navigate(['/secured/orders/orders-table'], {
            queryParams: {
              'tracking-number': item.resultText,
            },
          });
        }

        if (item.resultType === 'serial-numbers') {
          this.orderTrackingSharedDate.data.serialNumberQuery = item.resultText;
          this.router.navigate([orderTrackingTable], {
            queryParams: {
              'serial-number': item.resultText,
            },
          });
        }
      }
    }

    if (this.serviceAddon === 'managed-services') {
      if (item.navigationIdentifier) {
        this.router.navigate([managedServicesTickets, item.navigationIdentifier]);
      }
    }
  }

  /**
   * The function `onBrowseClicked()` emits an event and navigates to a specific route based on the
   * value of the `serviceAddon` variable.
   */
  onBrowseClicked() {
    this.onResultClickedEmitter.emit();
    if (this.serviceAddon === 'repair-services') {
      this.router.navigate([repairServicesTicketTableRoute]);
    }
    if (this.serviceAddon === 'order-tracking') {
      this.router.navigate([orderTrackingTable]);
    }

    if (this.serviceAddon === 'managed-services') {
      this.router.navigate([managedServicesTickets]);
    }
  }

  /**
   * The function takes a result text and a keyword as input, splits the result text into an array
   * based on the keyword, and returns the array.
   * @param {string} resultText - The `resultText` parameter is a string that represents the text that
   * needs to be formatted into an array.
   * @param {string} bestPrecisionKeyword - The `bestPrecisionKeyword` parameter is a string that
   * represents the keyword with the best precision.
   * @returns an array of strings.
   */
  formatResultTextIntoArray(resultText: string, bestPrecisionKeyword: string): string[] {
    const resultTextSplitted = resultText.toUpperCase().split(bestPrecisionKeyword.toUpperCase());
    const resultTextArray = [resultTextSplitted[0], bestPrecisionKeyword, resultTextSplitted[1]];
    return resultTextArray;
  }

  renderTitle(serviceAddon): string {
    switch (serviceAddon) {
      case 'repair-services':
        return 'LifecycleRepair';
      case 'order-tracking':
        return 'Order Tracking';
      case 'managed-services':
        return 'Managed Services';
      default:
        return 'Unknown';
    }
  }

  renderTitleIcon(serviceAddon): string {
    switch (serviceAddon) {
      case 'repair-services':
        return 'icon-glance-repair';
      case 'order-tracking':
        return 'icon-glance-assessment';
      case 'managed-services':
        return 'ms-icon-blue';
      default:
        return 'Unknown';
    }
  }

  renderBrowseTitle(serviceAddon): string {
    switch (serviceAddon) {
      case 'repair-services':
        return 'Tickets';
      case 'order-tracking':
        return 'Orders';
      case 'managed-services':
        return 'Managed Services';
      default:
        return 'Unknown';
    }
  }

  renderLinkText(navigationIdentifier?: string) {
    if (this.serviceAddon === 'repair-services') {
      if (navigationIdentifier) {
        return 'View Ticket';
      }
      return 'View Multiple Tickets Match';
    }

    if (this.serviceAddon === 'order-tracking') {
      if (navigationIdentifier) {
        return 'View PO';
      }
      return 'View Multiple Orders Match';
    }

    if (this.serviceAddon === 'managed-services') {
      if (navigationIdentifier) {
        return 'View Ticket';
      }
    }

    return 'Unknown';
  }

  renderNumberOfResults(total: number): string {
    if (total === 0 && !this.hasNoModulePermission) {
      return 'No Results';
    }
    if (total === 1) {
      return '1 Result';
    }
    if (total > 1 && total <= 5) {
      return `${total} Results`;
    }
    if (total > 5) {
      return '+5 Results';
    }

    return 'Unknown';
  }

  /**
   * The function takes an array of FilterType objects and returns a string representation of the found
   * filter types, separated by commas and with the last one separated by 'and'.
   * @param {FilterType[]} foundFilterTypes - An array of FilterType objects that have been found.
   * @returns The function `renderFoundFields` returns a string.
   */
  renderFoundFields(foundFilterTypes: FilterType[]): string {
    if (!foundFilterTypes) return '';
    if (foundFilterTypes.length === 0) {
      return '';
    }
    if (foundFilterTypes.length === 1) {
      return `${this.renderFilterType(foundFilterTypes[0])}`;
    }
    if (foundFilterTypes.length > 1) {
      //append with comma and the last with 'and'
      let foundFields = '';
      foundFilterTypes.forEach((field, index) => {
        if (index === foundFilterTypes.length - 1) {
          foundFields += ` and ${this.renderFilterType(field)}`;
        } else if (index === foundFilterTypes.length - 2) {
          foundFields += `${this.renderFilterType(field)}`;
        } else {
          foundFields += `${this.renderFilterType(field)}, `;
        }
      });
      return foundFields;
    }
    return '';
  }
}
